import { NgxMatMomentAdapter, NgxMatMomentModule } from '@angular-material-components/moment-adapter';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule } from 'keycloak-angular';
import { MomentModule } from 'ngx-moment';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { zip } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import gitInfo from '../git-version.json';
import { StpTranslateLoader } from '../stp-translate-loader';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AudioHostComponent } from './common/component/audio-host/audio-host.component';
import { ToastHostComponent } from './common/component/toast-host/toast-host.component';
import { SystemAlertBannerComponent } from './common/component/system-alert-banner/system-alert-banner.component';
import { LangService } from './common/service/lang.service';
import { WEBSOCKET_CONFIG_TOKEN } from './common/service/tokens';
import { UserProfileService } from './common/service/user-profile.service';
import { WebsocketClient } from './common/service/websocket-client.service';
import { InitErrorComponent } from './error/component/init-error/init-error.component';
import { InitErrorGuard } from './error/component/init-error/init-error.guard';
import { PageNotFoundComponent } from './error/component/page-not-found/page-not-found.component';
import { ServiceUnavailableComponent } from './error/component/service-unavailable/service-unavailable.component';
import { KeycloakAuthInterceptor } from './keycloak/interceptors/keycloak-auth-interceptor';
import { KeycloakInit } from './keycloak/keycloak-init';
import { UserSettingsService } from './settings/service/user-settings.service';
import { VehicleLayoutDictionaryService } from './settings/service/vehicle-layout-dictionary.service';
import { SharedModule } from './shared/shared.module';
import { StoreModule } from './store/store.module';
import { TopBarModule } from './top-bar/top-bar.module';
import { VehiclesConfigModule } from './vehicles-menu/vehicles-config.module';
import { SakpSharedModule } from './sakp/shared-sakp.module';
import { NGX_MAT_DATE_FORMATS } from '@angular-material-components/datetime-picker';
import { SideBarComponent } from './side-bar/side-bar.component';
import { CUSTOM_MOMENT_DATE_FORMATS, CUSTOM_MOMENT_DATETIME_FORMATS } from './shared/util/date-formats';

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new StpTranslateLoader(http, './assets/i18n/', '.json?' + gitInfo.hash);
}

export function init(keycloakInit: KeycloakInit, userProfileService: UserProfileService, userSettingsService: UserSettingsService
  , vehicleLayoutDictionaryService: VehicleLayoutDictionaryService, langService: LangService): () => Promise<any> {
  return (): Promise<any> => new Promise((resolve, reject) => {
    keycloakInit.initializer()
      .then(() => {
        keycloakInit.updateUserProfile$
          .pipe(
            concatMap(() => zip(
              userSettingsService.loadSettings(),
              userProfileService.getUserGroupsIds(),
              vehicleLayoutDictionaryService.loadVehicleLayoutDictionaries(),
              langService.setInitialLang()
            ))
          )
          .subscribe(
            () => {
              resolve(true);
            }, () => {
              resolve(false);
            }
          );
      })
      .catch(() => {
        reject();
      });
  });
}

@NgModule({
  declarations: [
    AppComponent,
    InitErrorComponent,
    PageNotFoundComponent,
    ServiceUnavailableComponent,
    ToastHostComponent,
    AudioHostComponent,
    SideBarComponent,
    SystemAlertBannerComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    KeycloakAngularModule,
    SharedModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    MomentModule,
    NgxMatMomentModule,
    StoreModule,
    ToastModule,
    VehiclesConfigModule,
    TopBarModule,
    SakpSharedModule.forRoot()
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: init,
      multi: true,
      deps: [KeycloakInit, UserProfileService, UserSettingsService, VehicleLayoutDictionaryService, LangService]
    },
    { provide: HTTP_INTERCEPTORS, useClass: KeycloakAuthInterceptor, multi: true },
    { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_MOMENT_DATETIME_FORMATS },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_MOMENT_DATE_FORMATS },
    { provide: DateAdapter, useClass: NgxMatMomentAdapter },
    WebsocketClient,
    { provide: WEBSOCKET_CONFIG_TOKEN, useValue: { address: environment.serverUrl, endpoint: 'stp2ext', serverName: 'stp2ext' } },
    InitErrorGuard,
    MessageService
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
